import { useState } from "react"
import { CountryFlagBadge } from "@/components/ui"
import { BackButton } from "@/components/controls/BackButton"

function countryCodeToEmoji(countryCode: string): string {
  const code = countryCode.toUpperCase()
  const offset = 0x1F1E6 - 65
  return String.fromCodePoint(
    code.charCodeAt(0) + offset,
    code.charCodeAt(1) + offset
  )
}

// ISO 3166-1 alpha-2 country codes
const COUNTRY_CODES = [
  // North America
  "US", "CA", "MX",
  // Central America & Caribbean
  "GT", "BZ", "HN", "SV", "NI", "CR", "PA", "CU", "JM", "HT", "DO", "PR", "BS", "BB", "TT",
  // South America
  "BR", "AR", "CL", "CO", "PE", "VE", "EC", "BO", "PY", "UY", "GY", "SR",
  // Western Europe
  "GB", "FR", "DE", "IT", "ES", "PT", "NL", "BE", "CH", "AT", "IE", "LU", "MC", "AD", "MT",
  // Northern Europe
  "SE", "NO", "DK", "FI", "IS",
  // Eastern Europe
  "PL", "CZ", "SK", "HU", "RO", "BG", "UA", "BY", "MD", "RS", "HR", "SI", "BA", "ME", "MK", "AL", "XK",
  // Baltic States
  "EE", "LV", "LT",
  // Russia & Central Asia
  "RU", "KZ", "UZ", "TM", "KG", "TJ",
  // Middle East
  "TR", "IL", "SA", "AE", "QA", "KW", "BH", "OM", "JO", "LB", "SY", "IQ", "IR", "YE",
  // South Asia
  "IN", "PK", "BD", "LK", "NP", "BT", "MV",
  // Southeast Asia
  "TH", "VN", "MY", "SG", "ID", "PH", "MM", "KH", "LA", "BN",
  // East Asia
  "CN", "JP", "KR", "KP", "TW", "HK", "MO", "MN",
  // Oceania
  "AU", "NZ", "FJ", "PG", "WS", "TO", "VU", "SB",
  // Africa
  "ZA", "EG", "NG", "KE", "ET", "GH", "TZ", "MA", "DZ", "TN", "LY", "SD", "UG", "RW", "SN", "CI", "CM", "AO", "MZ", "ZW", "BW", "NA", "MU", "MG",
]

export function FlagsDebugPage() {
  const [size, setSize] = useState(32)

  return (
    <div className="min-h-screen bg-gray-50 p-4">
      <div className="max-w-6xl mx-auto">
        <div className="flex items-center gap-3 mb-6">
          <BackButton to="/trips" size="sm" />
          <div>
            <h1 className="text-xl font-['Pioneer_Boulevard'] text-gray-900">
              Country Flag Badge Debug
            </h1>
            <p className="text-sm font-['Alga'] text-gray-600">
              {COUNTRY_CODES.length} countries
            </p>
          </div>
        </div>

        <div className="bg-white rounded-md border border-gray-200 p-4 mb-6">
          <label className="flex items-center gap-3">
            <span className="text-sm font-medium text-gray-700">Badge Size:</span>
            <input
              type="range"
              min="16"
              max="64"
              value={size}
              onChange={(e) => setSize(Number(e.target.value))}
              className="w-48"
            />
            <span className="text-sm text-gray-500 w-12">{size}px</span>
          </label>
        </div>

        <div className="bg-white rounded-md border border-gray-200 p-4">
          <div className="flex flex-wrap gap-3">
            {COUNTRY_CODES.map((code) => (
              <div
                key={code}
                className="flex flex-col items-center gap-1 p-2 rounded hover:bg-gray-50"
              >
                <CountryFlagBadge countryCode={code} size={size} />
                <span className="text-xs text-gray-500 font-mono">{code}</span>
                <span className="text-lg">{countryCodeToEmoji(code)}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="mt-6 bg-white rounded-md border border-gray-200 p-4">
          <h2 className="text-sm font-medium text-gray-900 mb-3">Usage Examples</h2>
          <div className="space-y-4">
            <div>
              <p className="text-xs text-gray-500 mb-2">Row of flags:</p>
              <div className="flex items-center gap-1">
                <CountryFlagBadge countryCode="US" size={20} />
                <CountryFlagBadge countryCode="FR" size={20} />
                <CountryFlagBadge countryCode="JP" size={20} />
                <CountryFlagBadge countryCode="BR" size={20} />
                <CountryFlagBadge countryCode="AU" size={20} />
              </div>
            </div>
            <div>
              <p className="text-xs text-gray-500 mb-2">With borders:</p>
              <div className="flex items-center gap-2">
                <CountryFlagBadge countryCode="GB" size={32} className="border border-gray-200 shadow-sm" />
                <CountryFlagBadge countryCode="DE" size={32} className="border border-gray-200 shadow-sm" />
                <CountryFlagBadge countryCode="IT" size={32} className="border border-gray-200 shadow-sm" />
              </div>
            </div>
            <div>
              <p className="text-xs text-gray-500 mb-2">Different sizes:</p>
              <div className="flex items-end gap-2">
                <CountryFlagBadge countryCode="CA" size={16} />
                <CountryFlagBadge countryCode="CA" size={24} />
                <CountryFlagBadge countryCode="CA" size={32} />
                <CountryFlagBadge countryCode="CA" size={48} />
                <CountryFlagBadge countryCode="CA" size={64} />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default FlagsDebugPage
